import { useMutation } from "@tanstack/react-query";
import { Mail, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { InterviewEmailPreview } from "@/components/interview-email-preview";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

interface SendInterviewEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  interviewId: string;
  applicationId: string;
  jobId?: string;
  jobTitle: string;
  stageName: string;
  stageDescription?: string;
  interviewerName: string;
  interviewerEmail?: string;
  durationMinutes: number;
  candidateName?: string;
}

export function SendInterviewEmailDialog({
  open,
  onOpenChange,
  interviewId,
  applicationId,
  jobId,
  jobTitle,
  stageName: initialStageName,
  stageDescription,
  interviewerName,
  interviewerEmail,
  durationMinutes,
  candidateName,
}: SendInterviewEmailDialogProps) {
  const { toast } = useToast();

  const [subject, setSubject] = useState("");
  const [introText, setIntroText] = useState("");
  const [emailStageName, setEmailStageName] = useState("");
  const [extraBody, setExtraBody] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [closingText, setClosingText] = useState("");

  useEffect(() => {
    if (open) {
      const sName = initialStageName || "Entrevista";
      const jTitle = jobTitle || "a vaga";
      setEmailStageName(sName);
      setSubject(`Próxima etapa: ${sName} — ${jTitle}`);
      setIntroText(
        `Temos uma novidade! Você avançou no processo seletivo para ${jTitle} e queremos te convidar para a próxima etapa.`,
      );
      setButtonText("Agendar Entrevista");
      setClosingText("Qualquer dúvida, é só responder este email.\nBoa sorte! 🍀");
      setExtraBody("");
    }
  }, [open, initialStageName, jobTitle]);

  const sendMutation = useMutation({
    mutationFn: async () => {
      return rpcClient.interviews.sendNotificationEmail({
        id: interviewId,
        subject,
        introText,
        stageName: emailStageName,
        extraBody,
        buttonText,
        closingText,
      });
    },
    onSuccess: () => {
      toast({ title: "Email enviado com sucesso" });
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId } })
          .queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.applications.list.queryOptions().queryKey });
      if (jobId) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({ input: { jobOpeningId: jobId } })
            .queryKey,
        });
      }
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({ title: "Erro ao enviar email", description: error.message, variant: "destructive" });
    },
  });

  const canSend =
    subject.trim().length > 0 && introText.trim().length > 0 && buttonText.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Enviar email de convite — {initialStageName}
          </DialogTitle>
        </DialogHeader>

        <div className="py-2">
          <InterviewEmailPreview
            candidateName={candidateName || "Candidato(a)"}
            subject={subject}
            onSubjectChange={setSubject}
            introText={introText}
            onIntroTextChange={setIntroText}
            stageName={emailStageName}
            onStageNameChange={setEmailStageName}
            interviewerName={interviewerName}
            interviewerEmail={interviewerEmail}
            durationMinutes={durationMinutes}
            stageDescription={stageDescription}
            extraBody={extraBody}
            onExtraBodyChange={setExtraBody}
            buttonText={buttonText}
            onButtonTextChange={setButtonText}
            closingText={closingText}
            onClosingTextChange={setClosingText}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => sendMutation.mutate()}
            disabled={!canSend || sendMutation.isPending}
          >
            <Send className="w-4 h-4 mr-1" />
            {sendMutation.isPending ? "Enviando..." : "Enviar Email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
